/* eslint-disable global-require */
import React, { useEffect, useState } from "react";
import { Form, Col, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

// graphql
import GET_LANGUAGE_DETAILS from "graphql/queries/GetLanguageDetails";
import GET_PROGRAM from "graphql/queries/GetProgram";
import { useQuery } from "@apollo/client";

// types
import { Program } from "types";

import { trackBeginCheckoutEvent } from "utils/gtm";

// utils
import countryOptions from "utils/countryOptions";

// common
import {
  ProgramInfoContainer,
  ProgramDescription,
} from "common/program_selector";
import { ModalLayout, MoreModalButtons } from "common/modal";
import PromoCode from "./promo_code";

// styling
import {
  NameOnCard,
  CardElementsStyling,
  CustomCardElement,
  BillingAddressInput,
  Image,
  StyledButton,
  Title,
  StyledSelect,
} from "./PaymentFormStyle";
import { Custom } from "./TotalAmountStyle";
import { CardTitle, FormCard } from "./SignUpStyle";

// Components
import FreeTrialPrice from "./FreeTrialPrice";
import TotalAmount from "./TotalAmount";
import { ErrorMessage } from "./SignUpFormStyle";
import NumberConverter from "./utils/NumberConverter";
import ReferralMessage from "./ReferralMessage";

const { ModalRow, ModalCol } = ModalLayout;
const { ButtonContainer, ModalButtonYellow } = MoreModalButtons;

type PaymentFormProps = {
  loading: boolean;
  language: string;
  paymentSuccess: boolean;
  chosenProgram: Program;
  setChosenProgram: (values: any) => void;
  productId: string;
  programType: string;
  studyLanguageId: string;
  currency: string;
};

const PaymentForm = ({
  loading,
  language,
  paymentSuccess,
  chosenProgram,
  setChosenProgram,
  productId,
  programType,
  studyLanguageId,
  currency,
}: PaymentFormProps) => {
  const { t } = useTranslation("new_sign_up");
  const [discount, setDiscount] = useState(0);
  const [validReferralCodeMessage, setValidReferralCodeMessage] = useState("");
  const [oneOnOneCredit, setOneOnOneCredit] = useState(0);
  const [smallGroupCredit, setSmallGroupCredit] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);
  const [classNumber, setClassNumber] = useState(0);
  const [freeClassNumber, setFreeClassNumber] = useState(0);
  const [haveFreeTrial, setHaveFreeTrial] = useState(false);
  const [isPackage, setIsPackage] = useState(false);

  const antIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "white" }} spin />
  );

  const {
    data: programData,
    loading: programQueryLoading,
    error: programQueryError,
  } = useQuery(GET_PROGRAM, {
    variables: {
      productId,
      currency: currency.toUpperCase(),
      language,
    },
  });

  const {
    data: languageData,
    loading: languageQueryLoading,
    error: languageQueryError,
  } = useQuery(GET_LANGUAGE_DETAILS, {
    variables: {
      id: studyLanguageId || "1",
    },
  });

  const location = useLocation();
  const showPaymentMethod = !location.pathname.includes("/vi");

  useEffect(() => {
    if (programData?.plan) {
      setChosenProgram(programData.plan);
      setTotalCredit(programData.plan.credits);
      setHaveFreeTrial(programData.plan.filter.includes("trial"));
      setIsPackage(programData.plan.type.includes("package"));

      // After user registered, move to next step show payment form
      // Track begin checkout to Google Analytics Ecommerce
      trackBeginCheckoutEvent(programData.plan);
    }
  }, [programData, setChosenProgram]);

  useEffect(() => {
    if (languageData) {
      // Change name of the variables as the variables are already declared above
      const { oneOnOneCredit: item1, smallGroupCredit: item2 } =
        languageData.languageDetails;
      setOneOnOneCredit(item1);
      setSmallGroupCredit(item2);
    }
  }, [languageData]);

  useEffect(() => {
    if (oneOnOneCredit && smallGroupCredit && totalCredit) {
      if (programType === "group") {
        setClassNumber(Math.floor(totalCredit / smallGroupCredit));
        setFreeClassNumber(3);
      } else {
        setClassNumber(Math.floor(totalCredit / oneOnOneCredit));
        setFreeClassNumber(1);
      }
    }
  }, [
    oneOnOneCredit,
    smallGroupCredit,
    totalCredit,
    classNumber,
    setClassNumber,
    setFreeClassNumber,
    programType,
  ]);

  const renderProgramName = () => {
    // if no plans found
    if (!programData?.plan) {
      return t("Program Not Found.");
    }
    if (programData?.plan) {
      return haveFreeTrial ? (
        <span>
          {t("7-Day Free Trial")} (
          <span>
            <NumberConverter value={freeClassNumber} display="initial" />
          </span>{" "}
          {programType === "group" ? t("Group Class") : t("One-On-One Class")}
          {freeClassNumber > 1 && language === "en" && "es"})
        </span>
      ) : (
        programData.plan.name
      );
    }

    return <span />;
  };

  const renderErrorMessage = (
    message: string,
    description: string | undefined
  ) => (
    <ErrorMessage
      message={message}
      description={description}
      type="error"
      showIcon
    />
  );

  const CourseDetails = (
    <>
      <ProgramInfoContainer>
        <ProgramDescription style={{ display: "block", color: "#000" }}>
          <div
            style={{
              whiteSpace: "normal",
              textAlign: "left",
              paddingRight: "10px",
            }}
          >
            {renderProgramName()}
          </div>
        </ProgramDescription>
      </ProgramInfoContainer>
      {haveFreeTrial && !programQueryLoading && !programQueryError && (
        <FreeTrialPrice
          chosenProgram={chosenProgram}
          programType={programType}
          classNumber={classNumber}
          language={language}
        />
      )}
      <TotalAmount
        classPrice={chosenProgram.price}
        discountAmount={discount}
        currency={chosenProgram.currency}
        haveFreeTrial={haveFreeTrial}
        isPackage={isPackage}
      />
      {!!validReferralCodeMessage && (
        <ReferralMessage message={validReferralCodeMessage} />
      )}
      <div style={{ width: "100%" }}>
        <PromoCode
          productId={productId}
          classPrice={chosenProgram.price}
          setDiscount={setDiscount}
          currency={chosenProgram.currency}
          setValidReferralCodeMessage={setValidReferralCodeMessage}
          hasFreeTrial={haveFreeTrial}
        />
      </div>

      {Boolean(discount) && haveFreeTrial && (
        <div
          style={{
            marginRight: "5px",
            fontSize: "120%",
            color: "#1070b6",
            fontWeight: "bold",
            textAlign: "start",
          }}
        >
          {t("You will not be charged anything today")}
        </div>
      )}
    </>
  );

  const PaymentMethod = (
    <>
      <ModalRow>
        <Col xs={24} sm={24} md={24} lg={18}>
          <Image>
            <a
              href="https://stripe.com/customers"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={require("assets/stripe-card.png")} alt="Stripe" />
            </a>
          </Image>
        </Col>
        <Col xs={0} sm={0} md={0} lg={6} />
        <ModalCol xs={0} sm={0} md={7} />
      </ModalRow>
      <Form.Item
        name="cardInfo"
        rules={[
          {
            required: true,
            message: t("Card info is required."),
          },
          () => ({
            validator(rule, value) {
              if (value && !value.complete) {
                return Promise.reject(new Error(t("Invalid card info.")));
              }
              return Promise.resolve();
            },
          }),
        ]}
      >
        <CustomCardElement options={CardElementsStyling} />
      </Form.Item>

      <ModalRow>
        <ModalCol xs={24}>
          <Form.Item
            name="cardholderName"
            rules={[
              {
                required: true,
                message: t("Card holder's name is required."),
              },
            ]}
          >
            <NameOnCard placeholder={t("Name on Card")} />
          </Form.Item>
        </ModalCol>
      </ModalRow>

      <ModalRow>
        <ModalCol xs={24}>
          <Form.Item
            name="billingLine1"
            rules={[
              {
                required: true,
                message: t("Billing address is required."),
              },
            ]}
          >
            <BillingAddressInput placeholder={t("Billing Address")} />
          </Form.Item>
        </ModalCol>
      </ModalRow>

      <ModalRow>
        <ModalCol xs={12}>
          <Form.Item
            name="billingCity"
            rules={[
              {
                required: true,
                message: t("City is required."),
              },
            ]}
          >
            <BillingAddressInput placeholder={t("City")} />
          </Form.Item>
        </ModalCol>
        <ModalCol xs={12}>
          <Form.Item
            name="billingPostalCode"
            rules={[
              {
                required: true,
                message: t("Postal code is required."),
              },
            ]}
          >
            <BillingAddressInput placeholder={t("Postal Code")} />
          </Form.Item>
        </ModalCol>
      </ModalRow>

      <ModalRow>
        <ModalCol xs={24}>
          <Form.Item
            name="billingCountry"
            rules={[
              {
                required: true,
                message: t("Country is required."),
              },
            ]}
          >
            <StyledSelect
              placeholder={t("Select Country")}
              options={countryOptions}
              showSearch
              size="large"
              filterOption={(input, option) =>
                (option?.label as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
              optionLabelProp="label"
            />
          </Form.Item>
        </ModalCol>
      </ModalRow>

      <ModalRow>
        <Col xs={24} sm={24} md={24} lg={18}>
          <Image>
            <a
              href="https://stripe.com/customers"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={require("assets/stripe-key.png")} alt="Stripe" />
            </a>
          </Image>
        </Col>
        <Col xs={0} sm={0} md={0} lg={6} />
      </ModalRow>
    </>
  );

  const PaymentButton = (
    <ModalButtonYellow
      type="primary"
      htmlType="submit"
      loading={loading}
      disabled={
        Boolean(programQueryError) ||
        Boolean(languageQueryError) ||
        Boolean(paymentSuccess)
      }
    >
      {t("CONFIRM PAYMENT")}
    </ModalButtonYellow>
  );

  const errorMessage = (
    <>
      {programQueryError &&
        renderErrorMessage("Error", programQueryError.message)}
      {languageQueryError &&
        renderErrorMessage("Error", languageQueryError.message)}
    </>
  );

  const handleGoogleClick = () => {
    window.open(
      "https://www.google.com/search?q=flexi+classes&oq=flexi+classes&gs_lcrp=EgZjaHJvbWUyBggAEEUYOTIGCAEQRRg8MgYIAhBFGEEyBggDEEUYQTIGCAQQRRhBMgYIBRBFGD3SAQgyMjExajBqMagCALACAQ&sourceid=chrome&ie=UTF-8#ip=1&lrd=0x31da19f1fe7d537d:0x9df67c53223ce2a2,1",
      "_blank",
      "popup"
    );
  };
  if (programQueryLoading || languageQueryLoading)
    return <Spin indicator={antIcon} />;
  if (window.innerWidth > 786)
    return (
      <>
        {errorMessage}
        <ModalRow gutter={16}>
          <ModalCol xs={24} sm={24} md={24} lg={8}>
            <CardTitle>{t("COURSE DETAILS").toUpperCase()}</CardTitle>
            <FormCard>
              {/** TODO: use query to get chosen programs */}
              {CourseDetails}
            </FormCard>
            {!showPaymentMethod && (
              <ModalRow>
                <ModalCol xs={1} sm={1} md={0} />
                <ButtonContainer xs={22} sm={22} md={24} lg={17}>
                  <Form.Item>{PaymentButton}</Form.Item>
                </ButtonContainer>
                <ModalCol xs={1} sm={1} md={0} lg={7} />
              </ModalRow>
            )}
          </ModalCol>

          {showPaymentMethod && (
            <ModalCol xs={24} sm={24} md={24} lg={11}>
              <CardTitle>{t("PAYMENT METHOD")}</CardTitle>
              <FormCard>{PaymentMethod}</FormCard>
              <ModalRow>
                <ModalCol xs={1} sm={1} md={0} />
                <ButtonContainer xs={22} sm={22} md={24} lg={17}>
                  <Form.Item>{PaymentButton}</Form.Item>
                </ButtonContainer>
                <ModalCol xs={1} sm={1} md={0} lg={7} />
              </ModalRow>
            </ModalCol>
          )}

          <ModalCol xs={24} sm={24} md={24} lg={5}>
            <ModalRow>
              <Image>
                <StyledButton type="button" onClick={handleGoogleClick}>
                  <img
                    src={require("assets/google-star-2.png")}
                    alt="Google Star"
                  />
                </StyledButton>
              </Image>
              <ModalCol xs={0} sm={0} md={7} />
            </ModalRow>
          </ModalCol>
        </ModalRow>
      </>
    );
  return (
    <>
      {errorMessage}
      <CardTitle>{t("COURSE DETAILS")}</CardTitle>
      <FormCard>
        {CourseDetails}
        <Custom />
        <Title>{t("PAYMENT METHOD")}</Title>
        {showPaymentMethod && PaymentMethod}
      </FormCard>
      <ModalRow>
        <ModalCol xs={1} sm={1} md={0} />
        <ButtonContainer xs={22} sm={22} md={24} lg={17}>
          <Form.Item>{PaymentButton}</Form.Item>
        </ButtonContainer>
        <ModalCol xs={1} sm={1} md={0} lg={7} />
      </ModalRow>
      <ModalRow>
        <Image>
          <StyledButton type="button" onClick={handleGoogleClick}>
            <img src={require("assets/google-star.png")} alt="Google Star" />
          </StyledButton>
        </Image>
        <ModalCol xs={0} sm={0} md={7} />
      </ModalRow>
    </>
  );
};

export default PaymentForm;
